import React, { useMemo } from 'react';
import { Paper, Typography, Box, Grid, Button, Divider } from '@mui/material';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';
import { APPLICATION_STATUS } from '../../../../../constants/event-application-status';
import { TranslucentContainer } from '../../../../../components';
import RetailApplicationCard from './RetailApplicationCard';
import { updateRetailApplicationStatus } from '../../../../../core/vendor-retail/action';

function RetailApplicationsView({ applications, onStatusChanged, loading }) {
  const dispatch = useDispatch();
  // Filter for pending applications only
  const pendingApplications = useMemo(
    () =>
      applications?.filter(
        (app) =>
          app.application_status === APPLICATION_STATUS.PENDING ||
          app.application_status === APPLICATION_STATUS.INCOMPLETE,
      ) || [],
    [applications],
  );

  const pendingGroups = useMemo(() => {
    const groups = new Map();
    pendingApplications.forEach((app) => {
      const vendorId = app.vendor?.id || 'unknown';
      const dateKey = app.booking_date ? moment(app.booking_date).format('YYYY-MM-DD') : 'unknown';
      const key = `${vendorId}|${dateKey}`;
      if (!groups.has(key)) {
        const vendorFirst = app.vendor?.name || 'Unknown Vendor';
        const vendorLast = app.vendor?.last_name ? ` ${app.vendor.last_name}` : '';
        const vendorFull = `${vendorFirst}${vendorLast}`;
        groups.set(key, {
          key,
          vendorName: vendorFull,
          date: dateKey,
          applications: [app],
          total: Number(app.total_price || 0),
        });
      } else {
        const g = groups.get(key);
        g.applications.push(app);
        g.total += Number(app.total_price || 0);
      }
    });
    const sorted = Array.from(groups.values()).sort((a, b) => {
      if (a.date < b.date) return -1;
      if (a.date > b.date) return 1;
      return 0;
    });
    return sorted.map((g) => {
      const displayDate = moment(g.date, 'YYYY-MM-DD').format('LL');
      return {
        ...g,
        displayDate,
        headerText: `${g.vendorName} • ${displayDate} • ${g.applications.length} item(s)`,
      };
    });
  }, [pendingApplications]);

  const approveGroup = (group) => {
    group.applications.forEach((app) => {
      dispatch(updateRetailApplicationStatus(app.id, APPLICATION_STATUS.APPROVED, onStatusChanged));
    });
  };

  if (loading) {
    return (
      <Grid container marginTop={5} justifyContent="center">
        <TranslucentContainer>
          <Box textAlign="center" py={8}>
            <Typography variant="h5" color="text.secondary">
              Loading applications...
            </Typography>
          </Box>
        </TranslucentContainer>
      </Grid>
    );
  }

  return (
    <Grid container marginTop={5} justifyContent="center">
      <TranslucentContainer>
        <Paper sx={{ backgroundColor: '#FCE5DD', p: 3, mb: 2, minHeight: '400px' }}>
          <Typography variant="h5" fontWeight="bold" color="#EE7B57" mb={3}>
            Retail Space Applications
          </Typography>

          {pendingGroups.length > 0 ? (
            pendingGroups.map((group) => (
              <Box key={group.key} mb={3}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {group.headerText}
                  </Typography>
                  <Box display="flex" alignItems="center" columnGap={2}>
                    <Typography variant="body2" color="text.secondary">
                      Total: ${group.total}
                    </Typography>
                    <Button variant="contained" size="small" onClick={() => approveGroup(group)}>
                      Approve group
                    </Button>
                  </Box>
                </Box>
                <Divider sx={{ mb: 2 }} />
                {group.applications.map((application) => (
                  <RetailApplicationCard
                    key={application.id}
                    application={application}
                    onStatusChanged={onStatusChanged}
                  />
                ))}
              </Box>
            ))
          ) : (
            <Box textAlign="center" py={8}>
              <Typography variant="h6" color="text.secondary">
                No pending applications
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={2}>
                New vendor retail space applications will appear here
              </Typography>
            </Box>
          )}
        </Paper>
      </TranslucentContainer>
    </Grid>
  );
}

RetailApplicationsView.propTypes = {
  applications: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      application_status: PropTypes.string,
      booking_date: PropTypes.string,
      start_time: PropTypes.string,
      end_time: PropTypes.string,
    }),
  ),
  onStatusChanged: PropTypes.func,
  loading: PropTypes.bool,
};

RetailApplicationsView.defaultProps = {
  applications: [],
  onStatusChanged: null,
  loading: false,
};

export default RetailApplicationsView;
